import { Fragment, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { line, Color, fill, SERVICE_REQUEST_SOURCE_LABELS, Size, formatDateTime, t, wallClockInZone, zoneAbbreviation, type ServiceRequestSource, type ServiceRequestStep, type StringKey } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Money } from '../../components/Money';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { post } from './api';
import { ServiceRequestEditModal } from './ServiceRequestEditModal';
import { SERVICE_REQUESTS_PATH } from './ServiceRequestFields';
import { BookVisitModal, type Booking } from './WorkOrderVisits';
import styles from './DealRequestBand.module.css';

/** The request band as the Deal page's answer carries it (decision 1912): cents and instants, worded here in the viewer's zone. */
export interface ServiceRequestBandView {
  /** 🔒 The account, whose workers the Raise Work Order window offers (decision 2814); absent from an older reply. */
  companyId?: string;
  /** 🔒 The request's need, read above the window's fields; absent from an older reply. */
  need?: string | null;
  receivedAt: string;
  takenByName: string;
  source: string;
  siteId: string;
  siteName: string;
  assetNodeId: string | null;
  assetName: string | null;
  preferredWindowStart: string | null;
  preferredWindowEnd: string | null;
  ceilingCents: number | null;
  minimumCents: number;
  tierName: string | null;
  /** 🔒 The tier's series key and, once the job is invoiced, why Edit is refused (decision 12600); absent from an older reply. */
  tierKey?: string | null;
  editProblem?: string | null;
  /** 🔒 The Deal's version the tile was read at, sent back with Edit to be compared (T3825); absent from an older reply. */
  version?: number;
  steps: { step: ServiceRequestStep; done: boolean; current: boolean }[];
  raiseProblem: string | null;
  offersQuote: boolean;
  /** 🔒 The Deal's owner, and whether the request still waits for a person to name one (decision 1914). */
  ownerName: string;
  ownerWaiting: boolean;
}

/** 🔨 Relative to the API's own root: Raise Work Order and Send a Quote First on one request (decision 1912). */
export function requestWorkOrderPath(dealId: string): string {
  return `${SERVICE_REQUESTS_PATH}/${dealId}/work-order`;
}

export function requestQuotePath(dealId: string): string {
  return `${SERVICE_REQUESTS_PATH}/${dealId}/quote`;
}

/** 🔨 The tile's Edit and the tiers it offers (decision 12600), relative to the API's own root. */
export function requestEditPath(dealId: string): string {
  return `${SERVICE_REQUESTS_PATH}/${dealId}`;
}

export function requestTiersPath(): string {
  return `${SERVICE_REQUESTS_PATH}/tiers`;
}

const STEP_WORDS: Readonly<Record<ServiceRequestStep, StringKey>> = {
  request: 'deal.request.step.request',
  workOrder: 'deal.request.step.workOrder',
  invoice: 'deal.request.step.invoice',
};

/**
 * 🔒 The request band (piece 18; decision 1912), drawn where the pipeline's stage
 * cards stand on any other Deal, beside the team, the contacts, the documents and
 * the history, which stay where they are. The route reads in three steps, the
 * request's own facts follow, and the actions are Raise Work Order with Send a
 * Quote First beside it as the secondary one. 🔨 The minimum is the tier's,
 * never typed, so the band words it and offers no input for it.
 */
export function DealRequestBand({ dealId, band, writes, apiBaseUrl = '', onError, onSaved = () => {} }: { dealId: string; band: ServiceRequestBandView; writes: boolean; apiBaseUrl?: string; onError: (message: string) => void; onSaved?: () => void }) {
  const navigate = useNavigate();
  const zone = useViewerZone();
  const format = useDateFormat();
  const [busy, setBusy] = useState(false);
  // 🔒 Decision 2814: Raise Work Order opens the booking window first, prefilled from the request's preferred window.
  const [raising, setRaising] = useState<Booking | null>(null);
  const [editing, setEditing] = useState(false);
  const prefill = (): Booking => ({
    startsAt: band.preferredWindowStart ? wallClockInZone(band.preferredWindowStart, zone) : '',
    endsAt: band.preferredWindowEnd ? wallClockInZone(band.preferredWindowEnd, zone) : '',
    workers: [],
    lead: '',
  });
  const when = (instant: string) => line(formatDateTime(instant, zone, format), zoneAbbreviation(instant, zone));
  const sourceWords = SERVICE_REQUEST_SOURCE_LABELS[band.source as ServiceRequestSource] ?? band.source;
  const window = band.preferredWindowStart
    ? line(when(band.preferredWindowStart), band.preferredWindowEnd ? `– ${when(band.preferredWindowEnd)}` : null)
    : t('deal.request.noWindow');
  const act = async (path: string, landing: (data: Record<string, string>) => string) => {
    setBusy(true);
    const answer = await post<Record<string, string>>(path, {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return onError(answer.message);
    navigate(landing(answer.data));
  };
  const facts: { key: string; label: string; value: ReactNode }[] = [
    { key: 'cameIn', label: t('deal.request.cameIn'), value: line(when(band.receivedAt), t('deal.request.takenBy'), band.takenByName, sourceWords ? '·' : null, sourceWords) },
    { key: 'site', label: t('deal.request.site'), value: <Link to={`/sites/${band.siteId}`}>{band.siteName}</Link> },
    { key: 'asset', label: t('deal.request.asset'), value: band.assetName && band.assetNodeId ? <Link to={`/assets/${band.assetNodeId}`}>{band.assetName}</Link> : t('deal.request.noAsset') },
    { key: 'owner', label: t('deal.request.owner'), value: band.ownerWaiting ? line(band.ownerName, '·', t('deal.request.ownerWaitingShort')) : band.ownerName },
    { key: 'window', label: t('deal.request.window'), value: window },
    { key: 'ceiling', label: t('deal.request.ceiling'), value: band.ceilingCents === null ? t('deal.request.ceilingNone') : <Money cents={band.ceilingCents} /> },
    { key: 'minimum', label: t('deal.request.minimum'), value: band.tierName ? <><Money cents={band.minimumCents} /> {fill(t('deal.request.minimumTier'), { tier: band.tierName ?? '' })}</> : t('deal.request.noTier') },
  ];
  return (
    <section className={styles.band} data-deal-request-band="true">
      <div className={styles.head}>
        <h3 className={styles.title}>{t('deal.request.band')}</h3>
        {writes ? <Button size={Size.Medium} color={Color.Inactive} text={[t('deal.request.edit')]} actions={[() => setEditing(true)]} disabled={Boolean(band.editProblem)} disabledReason={band.editProblem ?? ''} /> : null}
        <ol className={styles.steps} data-deal-request-steps="true">
          {band.steps.map((row) => (
            <li key={row.step} className={styles.step} data-request-step={row.step} data-request-step-state={row.done ? 'done' : row.current ? 'now' : 'todo'}>
              <span>{t(STEP_WORDS[row.step])}</span>
              {row.done ? <span>{t('deal.request.stepDone')}</span> : row.current ? <span>{t('deal.request.stepNow')}</span> : null}
            </li>
          ))}
        </ol>
      </div>
      <dl className={styles.facts}>
        {facts.map((fact) => (
          <Fragment key={fact.key}>
            <dt className={styles.factLabel}>{fact.label}</dt>
            <dd className={styles.factValue} data-deal-request-fact={fact.key}>{fact.value}</dd>
          </Fragment>
        ))}
      </dl>
      {writes && (band.raiseProblem === null || band.offersQuote) ? (
        <>
          <div className={styles.actions}>
            {band.raiseProblem === null ? <Button size={Size.Medium} color={Color.Navy} text={[t('deal.request.raise')]} actions={[() => setRaising(prefill())]} disabled={busy} disabledReason={t('deal.request.working')} /> : null}
            {band.offersQuote ? <Button size={Size.Medium} color={Color.Inactive} text={[t('deal.request.quoteFirst')]} actions={[() => void act(requestQuotePath(dealId), (data) => `/quotes/${data.quoteId}`)]} disabled={busy} disabledReason={t('deal.request.working')} /> : null}
          </div>
          <p className={styles.hint}>{t('deal.request.raiseHint')}</p>
        </>
      ) : null}
      {editing ? <ServiceRequestEditModal dealId={dealId} band={band} apiBaseUrl={apiBaseUrl} onCancel={() => setEditing(false)} onSaved={onSaved} /> : null}
      {raising ? (
        <BookVisitModal
          workOrderId=""
          companyId={band.companyId ?? ''}
          apiBaseUrl={apiBaseUrl}
          initial={raising}
          title={t('deal.request.raise')}
          raise={{ path: requestWorkOrderPath(dealId), need: band.need ?? null, siteId: band.siteId }}
          timeZone={zone}
          onClose={() => setRaising(null)}
          onBooked={(answer) => {
            setRaising(null);
            if (answer?.workOrderId) navigate(`/work-orders/${answer.workOrderId}`);
          }}
        />
      ) : null}
    </section>
  );
}
